<template>

  <v-container fluid class="pa-0">

    <v-form ref="readKeyForm" autocomplete="off" :disabled="componentDisabled" @submit.prevent="submit">

      <v-layout align-center>

        <app-select v-model="selectMedia" :items="keyMedias" return-object item-text="visibleName" required
          label="appSignDevicePrivateKeyReader.device" :disabled="componentDisabled" @change="selectMediaChanged">
          <template v-slot:append-outer>
            <app-tooltip>
              <v-btn :disabled="componentDisabled" icon small class="ma-0" @click="reloadDevices">
                <v-icon small>fa-sync-alt</v-icon>
              </v-btn>
              <template v-slot:tooltip>{{ $t('appSignDevicePrivateKeyReader.reloadDevice') }}</template>
            </app-tooltip>
          </template>
          <template v-slot:append-item="props">
            <v-list-item>
              <v-checkbox v-model="showAllDevices" class="app-checkbox ma-0 pa-0" hide-details
                :label="$t('appSignDevicePrivateKeyReader.showAll')" @change="onShowAllDevicesChanged" @click.stop>
              </v-checkbox>
            </v-list-item>
          </template>
        </app-select>

      </v-layout>

      <v-layout align-center>

        <app-password-field ref="password" v-model="keyPassword" :disabled="componentDisabled" required :minPasswordLen="1"
          label="appSignDevicePrivateKeyReader.password" autocomplete="on" name="device-signature-password" class="app-sign-text-field">
        </app-password-field>

        <v-btn v-if="keyIsReaded" color="warning" class="ml-2 mt-1" @click="onResetBtnClicked">
          {{ $t('buttons.removesign') }}
        </v-btn>
        <v-btn v-else type="submit" :disabled="componentDisabled" color="primary" class="ml-2 mt-1">
          {{ $t('appSignDevicePrivateKeyReader.read') }}
        </v-btn>

      </v-layout>

    </v-form>

    <div v-if="messageText" v-html="messageText" :class="[ 'info-panel pa-2', messageIsError ? 'error': 'success' ]">
    </div>

    <div v-if="warningText" v-html="warningText" class="info-panel pa-2 warning mt-1">
    </div>

  </v-container>

</template>

<script>
import AppPasswordField from '@/components/Common/AppPasswordField';
import AppSelect from '@/components/Common/AppSelect';
import AppTooltip from '@/components/Common/AppTooltip';
import AppSignBasePrivateKeyReader from './AppSignBasePrivateKeyReader';
import { logger } from '@/helpers/log.service';
import { actions as _act } from '@/models/store.actions';
import { deepClone } from '@/helpers';

export default {
  name: 'AppSignDevicePrivateKeyReader',
  extends: AppSignBasePrivateKeyReader,
  components: { AppPasswordField, AppSelect, AppTooltip },
  inject: ['isMobileOnly'],
  data() {
    return {
      keyMedias: [],
      allKeyMedias: [],
      selectMedia: null,
      useCtxEuSignFunctions: true,
      filterKeyMediasRegex: new RegExp(/(носій)|(файлова\sсистема)|(закордонний\sбіометричний\sпаспорт)/i)
    };
  },
  computed: {
    signingParamsRequestMsg() {
      return `${this.signingParamsRequestMsgBase}-DeviceSelector`;
    },
    signingParamsResponseMsg() {
      return `${this.signingParamsResponseMsgBase}-DeviceSelector`;
    },
    showAllDevices: {
      get: function() {
        return this.isSeal ? this.$store.getters[_act.SIGN_GET_SHOWALLDEVICES_SEAL] : this.$store.getters[_act.SIGN_GET_SHOWALLDEVICES];
      },
      set: function(value) {
        this.$store.commit(this.isSeal ? _act.SIGN_MUT_SHOWALLDEVICES_SEAL : _act.SIGN_MUT_SHOWALLDEVICES, { showAllDevices: value });
      }
    }
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      await this.euSignLibraryInitializedPromise;
      let storedSelectedDevice = this.getSelectedDeviceFromStore();
      if (storedSelectedDevice) {
        this.selectMedia = storedSelectedDevice;
      }
      this.reloadDevices();
    },
    async loadDevices() {
      this.allKeyMedias = [];
      if (this.euSignLibrary) {
        this.allKeyMedias = (await this.euSignLibrary.GetKeyMedias()) || [];
        logger.debug(`[AppSignDevicePrivateKeyReader:loadDevices] all devices`, this.allKeyMedias);
        this.keyMedias = this.showAllDevices ? this.allKeyMedias.slice() : this.filterKeyMedias();
        logger.debug(`[AppSignDevicePrivateKeyReader:loadDevices] filtered devices`, this.keyMedias);
        if (this.selectMedia) {
          if (!this.keyMedias.some(km => km.type === this.selectMedia.type && km.device === this.selectMedia.device)) {
            this.selectMedia = null;
          }
        }
        if (!this.selectMedia && this.keyMedias && this.keyMedias.length) {
          this.selectMedia = this.keyMedias[0];
        }
      } else {
        this.selectMedia = null;
      }
    },
    async readKey(certs) {
      let privateKeyContext = await this.euSignLibrary.CtxReadPrivateKey(
        this.euSignContext,
        Object.assign(this.selectMedia || {}, { password: this.keyPassword }),
        certs,
        this.certificateAuthority ? this.certificateAuthority.issuerCNs[0] : undefined
      );

      this.saveSelectedMediaToStore();

      return privateKeyContext;
    },
    reloadDevices() {
      let loader = this.showLoading();
      this.loadDevices()
        .catch((error) => this.handleError(error, 'appSignDevicePrivateKeyReader.errorDevice'))
        .finally(loader.hide);
    },
    async reset() {
      await this.resetBase();
    },
    async submit() {
      if (this.$refs.readKeyForm.validate()) {
        await this.readKeyClick();
      }
    },
    selectMediaChanged() {
      this.setFocusToPswControl();
    },
    setFocusToPswControl() {
      const inputs = this.$refs.password.$el.children[1].getElementsByTagName('input');
      if (inputs && inputs.length) {
        inputs[0].focus();
      }
    },
    async tryToReadKey() {
      await this.submit();
    },
    onPrepareComponent() {
      this.$refs.readKeyForm.resetValidation();
    },
    filterKeyMedias() {
      return this.allKeyMedias.filter(km => !this.filterKeyMediasRegex.test(km.type));
    },
    onShowAllDevicesChanged() {
      this.keyMedias = this.showAllDevices ? this.allKeyMedias : this.filterKeyMedias();
      this.$nextTick(() => {
        if (!this.keyMedias.some(km => km === this.selectMedia)) {
          this.selectMedia = null;
        }
        logger.debug(`[AppSignDevicePrivateKeyReader:onShowAllDevicesChanged] devices`, this.keyMedias);
        logger.debug(`[AppSignDevicePrivateKeyReader:onShowAllDevicesChanged] selected device`, this.selectMedia);
      });
    },
    getSelectedDeviceFromStore() {
      try {
        return this.isSeal ? this.$store.getters[_act.SIGN_GET_SELECTEDDEVICE_SEAL] : this.$store.getters[_act.SIGN_GET_SELECTEDDEVICE];
      } catch (e) {
        logger.error(e);
        return null;
      }
    },
    saveSelectedMediaToStore() {
      try {
        if (!this.selectMedia) return;

        let selectedDevice = deepClone(this.selectMedia);
        delete selectedDevice.password;

        this.$store.commit(this.isSeal ? _act.SIGN_MUT_SELECTEDDEVICE_SEAL : _act.SIGN_MUT_SELECTEDDEVICE, { selectedDevice });
      } catch (e) {
        logger.error(e);
      }
    }
  }
};
</script>
